<template>
  <a-modal
    :visible="visible"
    :title="$t('preInvoiceV2.khqrTitle')"
    width="560px"
    centered
    :zIndex="1022"
    :closable="false"
    :mask-closable="false"
    :keyboard="false"
    :footer="null"
  >
    <ts-loading-banner :loading="loading">
      <div class="khqr-modal-body">
        <div class="khqr-summary">
          <div>
            <span>{{ $t('preInvoiceV2.totalAmount') }}</span>
            <strong>{{ formattedAmount }}</strong>
          </div>
          <div>
            <span>{{ $t('preInvoiceV2.preInvoiceDate') }}</span>
            <strong>{{ invoiceDate || '-' }}</strong>
          </div>
        </div>

        <div v-if="!paymentAttempt" class="khqr-field">
          <label class="khqr-bank-label">
            {{ $t('preInvoiceV2.khqrBankAccount') }}
          </label>
          <a-radio-group v-model="selectedBankAccountId" class="khqr-bank-list">
            <label
              v-for="account in bankAccounts"
              :key="account.id"
              class="khqr-bank-option"
              :class="{ 'khqr-bank-option-active': String(selectedBankAccountId) === String(account.id) }"
            >
              <a-radio :value="account.id" />
              <span class="khqr-bank-main">
                <span class="khqr-bank-name">{{ account.bank_name || account.bank_name_kh || 'KHQR' }}</span>
                <span class="khqr-bank-account">
                  {{ account.account_name || '-' }}
                  <span v-if="account.account_number"> - {{ account.account_number }}</span>
                </span>
              </span>
              <span class="khqr-bank-currency">{{ account.currency_code }}</span>
            </label>
          </a-radio-group>
        </div>

        <a-alert
          v-if="!bankAccounts.length"
          type="warning"
          show-icon
          :message="$t('preInvoiceV2.noKhqrBankAccounts')"
        />

        <a-alert
          v-if="initFailedAfterSourceCreated && !paymentAttempt"
          type="warning"
          show-icon
          :message="$t('preInvoiceV2.khqrInitAfterSaveFailedTitle')"
          :description="$t('preInvoiceV2.khqrInitAfterSaveFailedDesc')"
        />

        <div v-if="paymentAttempt" class="khqr-result">
          <div class="khqr-result-head">
            <div>
              <span>{{ $t('preInvoiceV2.khqrStatus') }}</span>
              <strong>{{ statusLabel(paymentAttempt.status) }}</strong>
            </div>
            <div>
              <span>{{ $t('preInvoiceV2.khqrReference') }}</span>
              <strong>{{ paymentAttempt.provider_transaction_id || paymentAttempt.bill_number || '-' }}</strong>
            </div>
          </div>

          <div class="khqr-display-state">
            <div class="khqr-display-icon">
              <i class="fas fa-desktop"></i>
            </div>
            <div>
              <div class="khqr-display-bank">{{ selectedBankName }}</div>
              <div class="khqr-display-title">{{ $t('preInvoiceV2.khqrDisplayStatusTitle') }}</div>
              <div class="khqr-display-text">{{ $t('preInvoiceV2.khqrShownOnCustomerDisplay') }}</div>
            </div>
          </div>

          <a-alert
            v-if="paymentAttempt.message"
            :message="paymentAttempt.message"
            :type="paymentAttempt.status === 'failed' ? 'error' : 'info'"
            show-icon
          />

          <a v-if="paymentAttempt.deeplink" :href="paymentAttempt.deeplink" target="_blank" rel="noopener" class="khqr-deeplink">
            <i class="fas fa-external-link-alt"></i>
            {{ $t('preInvoiceV2.openQrDeeplink') }}
          </a>
        </div>
      </div>
    </ts-loading-banner>

    <div class="khqr-footer">
      <template v-if="!paymentAttempt">
        <a-button :disabled="loading" @click="close">
          {{ $t('preInvoiceV2.cancel') }}
        </a-button>
        <a-button
          type="primary"
          icon="qrcode"
          :loading="loading"
          :disabled="!canGenerate"
          @click="generateQr()"
        >
          {{ $t('preInvoiceV2.generateQr') }}
        </a-button>
      </template>
      <template v-else-if="paymentAttempt.status === 'pending'">
        <a-button :disabled="loading" @click="pollStatus">
          {{ $t('preInvoiceV2.refreshQrStatus') }}
        </a-button>
        <a-button type="danger" :loading="loading" @click="generateQr(true)">
          {{ $t('preInvoiceV2.regenerateQr') }}
        </a-button>
      </template>
    </div>
  </a-modal>
</template>

<script>
import notificationAlert from '@/utils/NotificationAlert'

export default {
  name: 'PreInvoiceKhqrPaymentModal',
  props: {
    visible: { type: Boolean, default: false },
    sourceId: { type: [Number, String], default: null },
    amount: { type: Number, default: 0 },
    invoiceDate: { type: String, default: null },
    bankAccounts: { type: Array, default: () => [] },
    resolveSourceId: { type: Function, default: null }
  },
  data() {
    return {
      loading: false,
      selectedBankAccountId: null,
      paymentAttempt: null,
      initFailedAfterSourceCreated: false,
      paymentSuccessEmitted: false,
      pollTimer: null
    }
  },
  computed: {
    canGenerate() {
      return Boolean(this.selectedBankAccountId && this.amount > 0 && !this.loading && !this.isPendingPayment)
    },
    isPendingPayment() {
      if (!this.paymentAttempt) return false
      if (this.paymentAttempt.status === 'pending') return true
      return this.paymentAttempt.status === 'paid' && ['pending', 'processing'].includes(this.paymentAttempt.fulfillment_status || 'pending')
    },
    isPaymentCompleted() {
      return this.paymentAttempt && this.paymentAttempt.status === 'paid' && this.paymentAttempt.fulfillment_status === 'completed'
    },
    isPaymentFailed() {
      return this.paymentAttempt && (
        ['failed', 'cancelled'].includes(this.paymentAttempt.status) ||
        (this.paymentAttempt.status === 'paid' && this.paymentAttempt.fulfillment_status === 'failed')
      )
    },
    selectedBankAccount() {
      return this.bankAccounts.find(account => String(account.id) === String(this.selectedBankAccountId)) || null
    },
    selectedBankName() {
      return (this.selectedBankAccount && (this.selectedBankAccount.bank_name || this.selectedBankAccount.bank_name_kh)) || 'KHQR'
    },
    formattedAmount() {
      return `$${Number(this.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.prepare()
      } else if (!this.isPendingPayment) {
        this.reset()
      }
    },
    bankAccounts() {
      if (!this.selectedBankAccountId) this.selectDefaultAccount()
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    prepare() {
      if (!this.isPendingPayment) {
        this.reset()
      }
      this.selectDefaultAccount()
    },
    reset() {
      const wasPaid = this.paymentAttempt && this.paymentAttempt.status === 'paid'
      this.stopPolling()
      this.paymentAttempt = null
      this.initFailedAfterSourceCreated = false
      this.paymentSuccessEmitted = false
      this.$emit('status-change', null)
      if (!wasPaid) {
        this.$emit('display-change', null)
      }
    },
    selectDefaultAccount() {
      if (this.bankAccounts.length) {
        this.selectedBankAccountId = this.bankAccounts[0].id
      }
    },
    statusLabel(status) {
      return this.$t(`khqrPayment.statuses.${status}`) === `khqrPayment.statuses.${status}` ? status : this.$t(`khqrPayment.statuses.${status}`)
    },
    buildDisplayPayload(attempt) {
      if (!attempt || !attempt.qr_payload) return null
      const account = this.selectedBankAccount || {}
      return Object.assign({}, attempt, {
        qr_payload: attempt.qr_payload,
        amount: Number(attempt.amount || this.amount || 0),
        bank_name: account.bank_name || account.bank_name_kh || null,
        account_name: account.account_name || null,
        currency_code: account.currency_code || null,
        bill_number: attempt.bill_number || null,
        status: this.isPendingPayment ? 'pending' : (attempt.status || null),
        fulfillment_status: attempt.fulfillment_status || null
      })
    },
    emitAttempt(attempt) {
      this.$emit('status-change', this.isPendingPayment ? 'pending' : (attempt ? attempt.status : null))
      const displayPayload = this.buildDisplayPayload(attempt)
      if (displayPayload) {
        this.$emit('display-change', displayPayload)
      }
    },
    emitPaymentSuccess() {
      if (this.paymentSuccessEmitted || !this.isPaymentCompleted) return
      this.paymentSuccessEmitted = true
      this.stopPolling()
      this.$emit('payment-success', this.buildDisplayPayload(this.paymentAttempt))
    },
    async resolvePaymentSourceId() {
      if (this.resolveSourceId) return await this.resolveSourceId()
      if (this.sourceId) return Number(this.sourceId)
      return null
    },
    async generateQr(isRegenerate = false) {
      if (!this.canGenerate && !isRegenerate) return
      this.loading = true
      this.stopPolling()
      this.initFailedAfterSourceCreated = false
      let resolvedSourceId = null
      try {
        resolvedSourceId = await this.resolvePaymentSourceId()
        if (!resolvedSourceId) {
          this.$notification.warning({
            message: this.$t('preInvoiceV2.khqrSaveFirstFailed'),
            style: { zIndex: 1031 }
          })
          return
        }

        const response = await this.$store.dispatch('preInvoiceV2/initKhqrPayment', {
          source_type: 'pre_invoice',
          source_id: Number(resolvedSourceId),
          amount: Number(this.amount || 0),
          bank_account_id: this.selectedBankAccountId,
          qr_source: 'qr'
        })
        this.paymentAttempt = response.data.data
        this.emitAttempt(this.paymentAttempt)
        this.$notification.success({
          message: this.$t('preInvoiceV2.success'),
          description: this.$t('preInvoiceV2.khqrGenerated'),
          style: { zIndex: 1031 }
        })
        if (this.isPaymentCompleted) {
          this.emitPaymentSuccess()
        } else if (this.isPendingPayment) {
          this.startPolling()
        }
      } catch (error) {
        if (resolvedSourceId) {
          this.initFailedAfterSourceCreated = true
        }
        notificationAlert(error)
        this.$emit('status-change', null)
        if (isRegenerate && this.paymentAttempt && this.paymentAttempt.status === 'pending') {
          this.startPolling()
        }
      } finally {
        this.loading = false
      }
    },
    async pollStatus() {
      const id = this.paymentAttempt && this.paymentAttempt.payment_attempt_id
      if (!id) return
      try {
        const response = await this.$store.dispatch('preInvoiceV2/getKhqrPaymentStatus', id)
        this.paymentAttempt = response.data.data
        this.emitAttempt(this.paymentAttempt)
        if (this.isPaymentCompleted) {
          this.emitPaymentSuccess()
        } else if (this.isPaymentFailed) {
          this.stopPolling()
          if (this.paymentAttempt.message) {
            this.$notification.error({
              message: this.$t('preInvoiceV2.error'),
              description: this.paymentAttempt.message,
              style: { zIndex: 1031 }
            })
          }
        }
      } catch (error) {
        this.stopPolling()
        notificationAlert(error)
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = window.setInterval(() => {
        this.pollStatus()
      }, 3000)
    },
    stopPolling() {
      if (this.pollTimer) {
        window.clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    close() {
      if (this.isPendingPayment) return
      this.reset()
      this.$emit('update:visible', false)
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.khqr-modal-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.khqr-summary,
.khqr-result-head {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.khqr-summary > div,
.khqr-result-head > div {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 10px;
}
.khqr-summary span,
.khqr-result-head span,
.khqr-bank-label,
.khqr-result-label {
  display: block;
  margin-bottom: 4px;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.khqr-summary strong,
.khqr-result-head strong {
  color: #0f172a;
  font-size: 15px;
}
.khqr-bank-list {
  display: grid;
  gap: 8px;
  width: 100%;
}
.khqr-bank-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}
.khqr-bank-option:hover {
  border-color: #93c5fd;
  background: #f8fafc;
}
.khqr-bank-option-active {
  border-color: #2563eb;
  background: #eff6ff;
  box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.08);
}
.khqr-bank-main {
  min-width: 0;
  flex: 1;
}
.khqr-bank-name {
  display: block;
  color: #1e293b;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
}
.khqr-bank-account {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
}
.khqr-bank-currency {
  flex: none;
  border-radius: 999px;
  background: #ecfdf5;
  color: #047857;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  padding: 5px 8px;
}
.khqr-result {
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #f8fbff;
  padding: 12px;
}
.khqr-display-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 18px 12px 10px;
  text-align: center;
}
.khqr-display-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  border-radius: 18px;
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  color: #64748b;
  font-size: 24px;
}
.khqr-display-bank {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.khqr-display-title {
  margin-top: 4px;
  color: #0f172a;
  font-size: 16px;
  font-weight: 800;
}
.khqr-display-text {
  margin: 4px auto 0;
  max-width: 340px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
}
.khqr-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 16px -24px -24px;
  padding: 10px 16px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
}
.khqr-deeplink {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  color: #2563eb;
  font-weight: 700;
}
@media (max-width: 640px) {
  .khqr-summary,
  .khqr-result-head {
    grid-template-columns: 1fr;
  }
}
</style>
